// Cometly saves its visitor token under this key. public/index.html writes a
// cross-domain `comet_token_override` here before the app can redirect it away.
export const COMET_TOKEN_STORAGE_KEY = "ctCartToken";

export const COMET_PIXEL_SELECTOR = 'script[data-ctag="cmt"], script[src*="m.inhouse.ai/script.js"]';

let cachedCometToken = null;
let cometPixelUnavailable = false;

// Pages a visitor can land on while logged out. Keep in sync with the inline
// pixel loader in public/index.html, which cannot import this file.
export function isCometPixelPath(pathname) {
  const path = pathname.replace(/\/+$/, "") || "/";
  return (
    path === "/signin" ||
    path === "/signup" ||
    path === "/try" ||
    path === "/lp" ||
    path.startsWith("/lp/") ||
    path.startsWith("/chat/shared/")
  );
}

// Query parameters that mark a click from an email or ad. Besides these, any
// utm_* parameter counts. Keep in sync with the inline loader in
// public/index.html.
const CAMPAIGN_PARAMS = ["comet_source", "comet_token_override", "_hsenc", "_hsmi", "fbclid", "gclid", "msclkid"];

export function hasCampaignParams(search) {
  try {
    for (const key of new URLSearchParams(search).keys()) {
      if (key.startsWith("utm_") || CAMPAIGN_PARAMS.includes(key)) return true;
    }
  } catch {
    // An unparsable query string carries no campaign.
  }
  return false;
}

// Campaign links often land on app pages (e.g. /document-library) where the
// pixel is otherwise paused, so Cometly never saw the click. Run it on that
// landing too; it pauses again once the visitor navigates on.
export function isCometPixelLocation(pathname, search) {
  return isCometPixelPath(pathname) || hasCampaignParams(search);
}

export function findCometScript() {
  const script = document.querySelector(COMET_PIXEL_SELECTOR);
  return script instanceof HTMLScriptElement ? script : null;
}

export function isCometPixelReady() {
  return typeof window.cometToken === "function";
}

// The pixel failed to load. Stop asking it, but keep using the stored token.
export function markCometPixelUnavailable() {
  cometPixelUnavailable = true;
}

function readStoredCometToken() {
  try {
    const stored = window.localStorage.getItem(COMET_TOKEN_STORAGE_KEY);
    return typeof stored === "string" && stored.trim() ? stored.trim() : null;
  } catch {
    return null;
  }
}

export function readCometToken() {
  if (cachedCometToken) return cachedCometToken;

  if (!cometPixelUnavailable && isCometPixelReady()) {
    try {
      const value = window.cometToken();
      if (typeof value === "string" && value.trim()) {
        cachedCometToken = value.trim();
        return cachedCometToken;
      }
      // The pixel returns its token synchronously. An empty result will not
      // become available later.
      cometPixelUnavailable = true;
    } catch {
      cometPixelUnavailable = true;
    }
  }

  // Pages without the pixel still send the token Cometly saved for this browser.
  return readStoredCometToken();
}

// Cometly passes its token between domains in this query parameter.
export const COMET_TOKEN_OVERRIDE_PARAM = "comet_token_override";

const INHOUSE_SITE_HOSTS = ["www.inhouse.ai", "inhouse.ai"];

/**
 * Adds the Comet token to a marketing-site URL, as the pixel does for its own
 * links. The pixel is paused on most app pages, so without this a visit to
 * www starts as a new Cometly visitor and is counted as Direct.
 */
export function withCometToken(url) {
  const token = readCometToken();
  if (!token) return url;

  try {
    const target = new URL(url, window.location.href);
    if (!INHOUSE_SITE_HOSTS.includes(target.host)) return url;
    if (target.searchParams.has(COMET_TOKEN_OVERRIDE_PARAM)) return url;
    target.searchParams.set(COMET_TOKEN_OVERRIDE_PARAM, token);
    return target.toString();
  } catch {
    return url;
  }
}

function decorateClickedLink(event) {
  const anchor = event.target instanceof Element ? event.target.closest("a[href]") : null;
  if (!anchor) return;

  const decorated = withCometToken(anchor.href);
  if (decorated !== anchor.href) anchor.href = decorated;
}

// One listener covers every link to the marketing site, including ones added
// later. It runs before navigation, so the token is read at click time.
export function installCometLinkDecorator() {
  document.addEventListener("click", decorateClickedLink, true);
  document.addEventListener("auxclick", decorateClickedLink, true);
  return () => {
    document.removeEventListener("click", decorateClickedLink, true);
    document.removeEventListener("auxclick", decorateClickedLink, true);
  };
}

export function resetCometTokenStateForTests() {
  cachedCometToken = null;
  cometPixelUnavailable = false;
}
